import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, toArray } from 'rxjs/operators';
import {
  getEmployeeVersionType,
  resolveEffectiveSettings,
} from '../entitlements/plan-transition-settings.util';
import { ProjectService } from './project.service';

export interface AdminControlEmployeeTarget {
  id?: number;
  name: string;
  email?: string;
}

export interface AdminControlMigrationResult {
  id: number;
  name: string;
  email: string;
  success: boolean;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminToEmployeeControlledService {
  constructor(private projectService: ProjectService) {}

  migrateEmployees(
    employees: AdminControlEmployeeTarget[]
  ): Observable<AdminControlMigrationResult[]> {
    const targets = employees.filter((employee) => !!employee.id);
    if (!targets.length) {
      return of([]);
    }

    return this.projectService.getGlobalSettings().pipe(
      switchMap((rawGlobal: any) => {
        const globalSettings =
          rawGlobal && typeof rawGlobal === 'object'
            ? (rawGlobal.data ?? rawGlobal)
            : {};

        return this.disableGlobalAdminControlToggles(globalSettings).pipe(
          switchMap(() =>
            from(targets).pipe(
              concatMap((employee) =>
                this.projectService.getEmployeeData(employee.id).pipe(
                  switchMap((fullUser: any) =>
                    this.migrateOne(fullUser || employee, globalSettings)
                  ),
                  catchError((error) =>
                    of({
                      id: employee.id as number,
                      name: employee.name,
                      email: employee.email || '',
                      success: false,
                      error:
                        error?.error?.message ||
                        error?.message ||
                        'Could not update tracking settings',
                    } satisfies AdminControlMigrationResult)
                  )
                )
              ),
              toArray()
            )
          )
        );
      }),
      map((results) => {
        if (results.some((result) => result.success)) {
          this.projectService.clearUserListCache();
        }
        return results;
      })
    );
  }

  private migrateOne(
    employee: any,
    globalSettings: Record<string, unknown>
  ): Observable<AdminControlMigrationResult> {
    const effective = resolveEffectiveSettings(employee, globalSettings);
    const displayName = employee.name || employee.slug || `Employee #${employee.id}`;
    const email = employee.email || employee.username || '';
    const settingsPayload = {
      ...globalSettings,
      ...effective,
      ss_automatic_tracking: 'false',
      ss_strict_time_tracking: 'false',
      ss_personalizeSettings: 'true',
      ss_periodic: String(effective.ss_periodic ?? globalSettings.ss_periodic ?? 'true'),
      ss_appchange: String(effective.ss_appchange ?? globalSettings.ss_appchange ?? 'false'),
      ss_webcam: String(effective.ss_webcam ?? globalSettings.ss_webcam ?? 'false'),
      ss_silent: String(effective.ss_silent ?? globalSettings.ss_silent ?? 'false'),
      ss_idle_time: String(effective.ss_idle_time ?? globalSettings.ss_idle_time ?? '00:05:00'),
      ss_screen_capture_time: String(
        effective.ss_screen_capture_time ?? globalSettings.ss_screen_capture_time ?? '00:10:00'
      ),
      ss_appchange_interval: String(
        effective.ss_appchange_interval ?? globalSettings.ss_appchange_interval ?? '00:01:00'
      ),
    };

    const versionType = getEmployeeVersionType(employee);
    const requests: Observable<unknown>[] = [
      this.projectService.setGlobalSettings(settingsPayload, employee.id),
    ];

    if (versionType === 'restricted' || versionType === 'r') {
      requests.push(
        this.projectService.updateEmployeeVersionType(employee.id as number, 'general')
      );
    }

    return forkJoin(requests).pipe(
      map(() => ({
        id: employee.id as number,
        name: displayName,
        email,
        success: true,
      }))
    );
  }

  private disableGlobalAdminControlToggles(
    globalSettings: Record<string, unknown>
  ): Observable<unknown> {
    const autoOn = String(globalSettings?.ss_automatic_tracking ?? '')
      .trim()
      .toLowerCase();
    const strictOn = String(globalSettings?.ss_strict_time_tracking ?? '')
      .trim()
      .toLowerCase();

    if (autoOn !== 'true' && autoOn !== '1' && strictOn !== 'true' && strictOn !== '1') {
      return of(null);
    }

    return this.projectService.setGlobalSettings(
      {
        ...globalSettings,
        ss_automatic_tracking: 'false',
        ss_strict_time_tracking: 'false',
      },
      false
    );
  }
}
